import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Mail, AlertCircle, Inbox } from "lucide-react";

import { apiFetch } from "@/lib/api";

interface EmailLog {
  id: number;
  order_id: number | null;
  email_to: string;
  email_type: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

export default function Emails() {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const params = new URLSearchParams();
  if (statusFilter) params.set("status", statusFilter);
  params.set("offset", String(page * limit));
  params.set("limit", String(limit));

  const { data, isLoading, error } = useQuery<EmailLog[]>({
    queryKey: ["admin", "email-logs", statusFilter, page],
    queryFn: () => apiFetch<EmailLog[]>(`/api/admin/email-logs?${params.toString()}`),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Email logovi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pregled poslanih i neuspjelih emailova
          </p>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-2">
        {[
          { value: "", label: "Svi" },
          { value: "sent", label: "Poslano" },
          { value: "failed", label: "Neuspjelo" },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setStatusFilter(opt.value); setPage(0); }}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              statusFilter === opt.value
                ? "bg-foreground text-white border-foreground"
                : "bg-white text-muted-foreground border-border hover:bg-muted/50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle className="size-4 shrink-0" />
          Greška pri učitavanju email logova
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Narudžba</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="px-4 py-3"><div className="h-4 w-12 bg-muted rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 w-40 bg-muted rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-5 w-14 bg-muted rounded animate-pulse" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && data && data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Inbox className="size-12 mb-3 text-muted-foreground/40" />
          <p className="text-sm">Nema email logova</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && data && data.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Narudžba</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((log) => (
                <tr key={log.id} className="border-b border-border/60 last:border-b-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm">
                    {log.order_id != null ? (
                      <Link
                        to="/admin/orders/$id"
                        params={{ id: String(log.order_id) }}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        #{log.order_id}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{log.email_to}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                        log.status === "sent"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      {log.status === "sent" ? "Poslano" : "Neuspjelo"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && data && data.length >= limit && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-white text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Prethodna
          </button>
          <span className="text-sm text-muted-foreground">{page + 1}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-white text-muted-foreground hover:bg-muted/50 transition-colors"
          >
            Sljedeća
          </button>
        </div>
      )}
    </div>
  );
}
